import { ICustomizationImageEntity, ICustomizationImageEntityAttribute } from "epics/customizations-epic";
import {
    CustomizationImageAttributeKind,
    CustomizationImageFilterDictionary,
    getDefaultMetaCustomizationSelections,
    IAttribute,
    ImagePermissionType,
    IPrintedImagePermission,
    mapByKey,
} from "constants/product-customizations/customization";
import {
    TCustomizationSelection,
    TMetaCustomizationSelection,
} from "constants/product-customizations/customization-selection.types";
import {
    FetchSourceTypes,
    ICustomizationImageTag,
} from "constants/product-customizations/customization-image-tag-schema";

import { IProduct } from "records/item/item";
import { ICompany } from "../../contacts/contacts-types";
import _ from "lodash";
import { assertExhaustiveSwitch } from "components/utils/validations";

type PartialMe = {
    isBuyer: () => boolean;
    isBrandUser: () => boolean;
    isNuOrderUser: () => boolean;
};

const getUserPermission = (permissions: IPrintedImagePermission[], type: ImagePermissionType) =>
    permissions?.find((permission) => ImagePermissionType[permission.type] === type);

export const getUserPermissions = (
    permissions: IPrintedImagePermission[]
): {
    buyerPermission?: IPrintedImagePermission;
    brandPermission?: IPrintedImagePermission;
    buyerCanUpload: boolean;
    brandCanUpload: boolean;
} => {
    const buyerPermission = getUserPermission(permissions, ImagePermissionType.BUYER);
    const brandPermission = getUserPermission(permissions, ImagePermissionType.BRAND);
    const buyerCanUpload = buyerPermission?.canUploadImage || false;
    const brandCanUpload = brandPermission?.canUploadImage || false;

    return { buyerPermission, brandPermission, buyerCanUpload, brandCanUpload };
};

export const getInitialImageGroup = (permissions: IPrintedImagePermission[]): ImagePermissionType =>
    permissions?.length === 1 && Number(ImagePermissionType[permissions?.[0]?.type]) === ImagePermissionType.BRAND
        ? ImagePermissionType.BRAND
        : ImagePermissionType.BUYER;

export const getCanUploadImages = (
    selectedTab: ImagePermissionType,
    permissions: IPrintedImagePermission[],
    me: PartialMe
): boolean => {
    const isBuyerUser = me.isBuyer();
    const isBrandUser = me.isBrandUser() || me.isNuOrderUser();

    const { buyerCanUpload, brandCanUpload } = getUserPermissions(permissions);

    const brandUserCanUpload = brandCanUpload && selectedTab === ImagePermissionType.BRAND && isBrandUser;
    const buyerUserCanUpload =
        buyerCanUpload && selectedTab === ImagePermissionType.BUYER && (isBuyerUser || isBrandUser);

    return brandUserCanUpload || buyerUserCanUpload;
};

export const getImageListFilters = (
    tags: ICustomizationImageTag[],
    attribute?: IAttribute | undefined,
    attributeSelection?: TCustomizationSelection | undefined,
    company?: Partial<ICompany> | undefined,
    product?: Partial<IProduct> | undefined
): CustomizationImageFilterDictionary =>
    tags.reduce((map, tag) => {
        const { filter, externalId: key } = tag;
        if (!filter) {
            return map;
        }

        const setValue = (value: number | string | boolean | undefined | null) => {
            if (value != undefined) {
                map[key] = value;
            }
        };

        const { fetchSource, constantSource } = filter;
        if (fetchSource) {
            const { keypath, type } = fetchSource;
            const { CUSTOMIZATION_ATTRIBUTE, CUSTOMIZATION_SELECTION, PRODUCT, PRODUCT_FDM, COMPANY_FDM, COMPANY } =
                FetchSourceTypes;

            const get = <T>(value: T, ...types: FetchSourceTypes[]): T | null => (types.includes(type) ? value : null);
            const source: any | null =
                get(product, PRODUCT, PRODUCT_FDM) ??
                get(attributeSelection, CUSTOMIZATION_SELECTION) ??
                get(attribute, CUSTOMIZATION_ATTRIBUTE) ??
                get(company as ICompany, COMPANY, COMPANY_FDM);

            setValue(_.get(source?.toJSON?.() ?? source, keypath.split(".")));
        }

        if (constantSource) {
            setValue(constantSource.string ?? constantSource.number ?? constantSource.boolean);
        }

        return map;
    }, {} as CustomizationImageFilterDictionary);

const customizationImageEntityAttributeValue = (attribute: ICustomizationImageEntityAttribute) => {
    switch (attribute.kind) {
        case CustomizationImageAttributeKind.STRING:
            return attribute.stringValue;

        case CustomizationImageAttributeKind.BOOL:
            return attribute.boolValue ? "true" : "false";

        case CustomizationImageAttributeKind.ARRAY:
            return attribute.arrayValueList.join(",");

        case CustomizationImageAttributeKind.FLOAT:
            return `${attribute.floatValue}`;

        case CustomizationImageAttributeKind.NULL:
            return "";

        default:
            assertExhaustiveSwitch(attribute.kind);
    }
};

export const customizationImageEntitiesWithAttributes = (
    contextImages: unknown,
    tagsDictionary: _.Dictionary<ICustomizationImageTag>
) => {
    if (!Array.isArray(contextImages)) {
        return [];
    }

    const result: ICustomizationImageEntity[] = contextImages;
    if (_.isEmpty(tagsDictionary)) {
        return result;
    }

    // TODO: image can be null for some reason
    for (const image of result) {
        if (!image || image.customizationAttributes) {
            continue;
        }

        image.customizationAttributes = image.attributesList
            ?.map((attribute) => {
                const attributeId = attribute.name;
                const tag = tagsDictionary[attributeId];
                if (!tag) {
                    return;
                }

                const value = customizationImageEntityAttributeValue(attribute);
                if (!value) {
                    return;
                }

                return {
                    attributeId: tag._id,
                    externalId: tag.externalId,
                    name: tag.name,
                    value,
                    isVisible: tag.isVisible,
                    isApiFormattable: tag.isApiFormattable,
                };
            })
            .filter(Boolean);
    }

    return result;
};

export const getMetaNestedValues = (
    nestedValues: TMetaCustomizationSelection[],
    selectedNestedValues: TMetaCustomizationSelection[],
    readonly?: boolean,
    childAttributes?: IAttribute[]
) => {
    let initialValues = [...nestedValues];
    if (childAttributes && !nestedValues?.length) {
        initialValues = getDefaultMetaCustomizationSelections(childAttributes);
    }

    if (readonly) {
        initialValues = selectedNestedValues;
    }

    const selectedNestedValuesMap = mapByKey(selectedNestedValues, "attributeId");
    return initialValues.map((value) => selectedNestedValuesMap.get(value.attributeId) ?? value);
};
